/**
 * Vello theme mechanics (S055; IMPORT-0001 §3/§4, extraction contract §1).
 *
 * A `Theme` bundles the theme-varying tokens (color roles, shadows, focus ring) for one of the two themes.
 * The theme-invariant tokens (brand, gray ramp, spacing, radius, type, motion) live in their own modules.
 * `cssVariablesFor` emits the full CSS custom-property map a host applies to `:root` (light) or
 * `[data-theme="dark"]`, so the design tokens drive real CSS without any consumer hardcoding a value.
 *
 * The focus is the source's single soft halo plus an ink edge (`--focus-ring`, Claude Design set/2026 — it replaced
 * the old double 2px ring; owner D424): never two rings with a gap between them.
 */
import { LIGHT_COLORS, DARK_COLORS, APP_DARK_COLORS, WEB_DARK_COLORS, WEB_LIGHT_COLORS, GRAY, BRAND } from './color.js';
import { LIGHT_SHADOWS, DARK_SHADOWS, SCRIM_BLUR } from './shadow.js';
import { TYPE_ROLES, FONT_FAMILIES } from './typography.js';
import { SPACE, SPACE_FINE, LAYOUT, TAP_TARGET_MIN, CONTROL_VISUAL, CONTROL_LARGE } from './space.js';
import { RADIUS } from './radius.js';
import { EASING, DURATION, REDUCED_MOTION_FADE_MAX_MS, STAGGER_STEP_MS, STAGGER_MAX_ITEMS } from './motion.js';
import { LIGHT_CHART_COLORS, DARK_CHART_COLORS, LIGHT_CHART_SCALARS, DARK_CHART_SCALARS, CHART_TONE_ROLES, chartColorVar, chartStaticVariables, chartToneVar, } from './charts.js';
/**
 * The focus edge's geometry on the web (px): a 2px outline drawn flush INSIDE the control (offset = -width), so the
 * edge replaces the control's border instead of floating 2px away from it — the gap that made the old double ring (D424).
 */
export const FOCUS_RING = {
    widthPx: 2,
    offsetPx: -2,
};
export const LIGHT_THEME = {
    name: 'light',
    colors: LIGHT_COLORS,
    shadows: LIGHT_SHADOWS,
    focusRing: '0 0 0 3px rgba(35, 48, 72, 0.15)',
    focusRingInverse: '0 0 0 3px rgba(255, 255, 255, 0.28)',
    focusRingSoft: '0 0 0 3px rgba(35, 48, 72, 0.08)',
    focusRingColor: '#233048',
    charts: LIGHT_CHART_COLORS,
    chartScalars: LIGHT_CHART_SCALARS,
};
/**
 * The web consoles' light theme (D854, DS-1): {@link LIGHT_THEME} with the tertiary text of {@link WEB_LIGHT_COLORS} lifted to AA.
 * `installVelloTheme()` and `velloThemeCss()` default to it; the native apps keep {@link LIGHT_THEME}.
 */
export const WEB_LIGHT_THEME = {
    ...LIGHT_THEME,
    colors: WEB_LIGHT_COLORS,
};
export const DARK_THEME = {
    name: 'dark',
    colors: DARK_COLORS,
    shadows: DARK_SHADOWS,
    focusRing: '0 0 0 3px rgba(255, 255, 255, 0.28)',
    focusRingInverse: '0 0 0 3px rgba(35, 48, 72, 0.15)',
    focusRingSoft: '0 0 0 3px rgba(255, 255, 255, 0.14)',
    focusRingColor: '#FFFFFF',
    charts: DARK_CHART_COLORS,
    chartScalars: DARK_CHART_SCALARS,
};
/**
 * The native apps' dark theme (D763, Claude Design "App Cliente - Dark Theme Revisão" 1a): the web dark with the raised
 * navy surfaces of {@link APP_DARK_COLORS}. Focus, shadows and charts are the web dark's.
 */
export const APP_DARK_THEME = {
    ...DARK_THEME,
    colors: APP_DARK_COLORS,
};
/**
 * The web consoles' dark theme (D798; owner 2026-10-28: "aplicar aquele padrão de Navy que foi aplicado nos apps"): the
 * native apps' navy ({@link APP_DARK_THEME}), so a console and an app look alike in the dark, with the two text roles of
 * {@link WEB_DARK_COLORS} lifted for AA on the console's cards. The Claude Design web dark ({@link DARK_THEME}, page
 * `#0E1626`) stays the Painel TV's wall.
 */
export const WEB_DARK_THEME = {
    ...APP_DARK_THEME,
    colors: WEB_DARK_COLORS,
};
export function themeFor(name) {
    return name === 'dark' ? DARK_THEME : LIGHT_THEME;
}
/**
 * Stacking order. Only these named layers exist — a consumer never invents a raw z-index. Active-navigation
 * sheets sit above a full-bleed map; a modal and toast sit above everything (IMPORT-0001 §4).
 */
export const Z_INDEX = {
    base: 0,
    map: 10,
    sheet: 100,
    header: 200,
    overlay: 900,
    modal: 1000,
    toast: 1100,
};
const roleVar = (role) => `--color-${role.replace(/\./g, '-')}`;
/**
 * Emit the full CSS custom-property map for a theme. The host writes these onto `:root` for the light theme
 * and onto `[data-theme="dark"]` for the dark theme. Includes the theme-varying tokens plus the invariant
 * tokens (so a single application call yields a complete variable set).
 */
export function cssVariablesFor(theme) {
    const vars = {};
    for (const [role, value] of Object.entries(theme.colors))
        vars[roleVar(role)] = value;
    for (const [step, value] of Object.entries(theme.shadows))
        vars[`--shadow-${step}`] = value;
    vars['--focus-ring'] = theme.focusRing;
    vars['--focus-ring-inverse'] = theme.focusRingInverse;
    vars['--focus-ring-soft'] = theme.focusRingSoft;
    vars['--focus-ring-color'] = theme.focusRingColor;
    vars['--focus-ring-width'] = `${FOCUS_RING.widthPx}px`;
    vars['--focus-ring-offset'] = `${FOCUS_RING.offsetPx}px`;
    // Chart tokens (D507): the series/grid/axis/track/tooltip colours, the area-fill opacity, and the semantic tone aliases
    // (a chart marks a STATE with the accessible `*-text` roles, never with the base brand red/green/amber).
    for (const [role, value] of Object.entries(theme.charts))
        vars[chartColorVar(role)] = value;
    vars['--chart-area-opacity'] = `${theme.chartScalars.areaOpacity}`;
    for (const tone of Object.keys(CHART_TONE_ROLES))
        vars[chartToneVar(tone)] = `var(${roleVar(CHART_TONE_ROLES[tone])})`;
    Object.assign(vars, chartStaticVariables());
    // Theme-invariant tokens (identical across light/dark; emitted for a complete set).
    for (const [step, value] of Object.entries(GRAY))
        vars[`--gray-${step}`] = value;
    vars['--brand-red'] = BRAND.red;
    vars['--brand-green'] = BRAND.green;
    vars['--font-sans'] = FONT_FAMILIES.sans;
    vars['--font-mono'] = FONT_FAMILIES.mono;
    for (const [name, role] of Object.entries(TYPE_ROLES)) {
        vars[`--text-${name}-size`] = `${role.sizePx}px`;
        vars[`--text-${name}-leading`] = `${role.leading}`;
        vars[`--text-${name}-weight`] = `${role.weight}`;
        if (role.trackingEm !== undefined)
            vars[`--text-${name}-tracking`] = `${role.trackingEm}em`;
        if (role.mono === true)
            vars[`--text-${name}-family`] = 'var(--font-mono)';
    }
    for (const [step, value] of Object.entries(SPACE))
        vars[`--space-${step}`] = `${value}px`;
    for (const [step, value] of Object.entries(SPACE_FINE))
        vars[`--space-fine-${step}`] = `${value}px`;
    for (const [layer, value] of Object.entries(SCRIM_BLUR))
        vars[`--scrim-blur-${layer}`] = `${value}px`;
    vars['--page-gutter-mobile'] = `${LAYOUT.pageGutterMobile}px`;
    vars['--page-gutter-desktop'] = `${LAYOUT.pageGutterDesktop}px`;
    vars['--content-max'] = `${LAYOUT.contentMax}px`;
    vars['--tap-target-min'] = `${TAP_TARGET_MIN}px`;
    for (const [name, value] of Object.entries(CONTROL_VISUAL))
        vars[`--control-${name.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`] = `${value}px`;
    for (const [name, value] of Object.entries(CONTROL_LARGE))
        vars[`--control-${name}`] = `${value}px`;
    for (const [step, value] of Object.entries(RADIUS))
        vars[`--radius-${step}`] = `${value}px`;
    for (const [name, value] of Object.entries(EASING))
        vars[`--ease-${name}`] = value;
    for (const [name, value] of Object.entries(DURATION))
        vars[`--duration-${name}`] = `${value}ms`;
    vars['--motion-reduced-fade'] = `${REDUCED_MOTION_FADE_MAX_MS}ms`;
    vars['--motion-stagger'] = `${STAGGER_STEP_MS}ms`;
    vars['--motion-stagger-max'] = `${STAGGER_MAX_ITEMS}`;
    return vars;
}
/** Render a theme's variables as a CSS rule body (`--x: y;`), for a `:root { … }` block. */
export function renderCssVariables(theme) {
    return Object.entries(cssVariablesFor(theme))
        .map(([k, v]) => `  ${k}: ${v};`)
        .join('\n');
}
//# sourceMappingURL=theme.js.map